import { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";
import { notifyConsentChanged, track } from "@/lib/analytics";

const KEY = "ksdevarc_cookie_consent";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(KEY);
    if (!stored) {
      const t = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(t);
    }
    const open = () => setVisible(true);
    window.addEventListener("ksdevarc:open-cookie-settings", open);
    return () => window.removeEventListener("ksdevarc:open-cookie-settings", open);
  }, []);

  const decide = (choice) => {
    localStorage.setItem(
      KEY,
      JSON.stringify({ choice, ts: new Date().toISOString() })
    );
    setVisible(false);
    notifyConsentChanged();
    if (choice === "accepted") {
      // Small delay so gtag.js has a chance to load before the first event
      setTimeout(() => track("cookie_consent", { choice }), 350);
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent"
      role="dialog"
      aria-label="Cookie consent"
      className="fixed z-[60] left-4 right-4 bottom-4 sm:left-auto sm:bottom-6 sm:right-6 sm:max-w-md"
    >
      <div className="relative border border-[var(--border-dim)] bg-[var(--bg-2)] rounded-md shadow-xl overflow-hidden">
        <div className="absolute -top-12 -right-12 w-32 h-32 bg-[var(--vermilion)] opacity-10 blur-3xl pointer-events-none" />

        <button
          aria-label="Close"
          data-testid="cookie-consent-close"
          onClick={() => decide("dismissed")}
          className="absolute top-3 right-3 w-7 h-7 rounded-full border border-[var(--border-dim)] flex items-center justify-center text-[var(--text-3)] hover:text-[var(--vermilion)] hover:border-[var(--vermilion)] transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        <div className="relative p-5 sm:p-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[var(--border-dim)] bg-[var(--bg)] text-[var(--lime)]">
              <Cookie className="w-4 h-4" />
            </span>
            <div className="overline">Cookies & Privacy</div>
          </div>

          <h3 className="font-display font-bold text-lg tracking-tight">
            We use cookies to make this site work better.
          </h3>
          <p className="mt-2 text-[var(--text-2)] text-sm leading-relaxed">
            We use essential and analytics cookies to improve functionality and
            understand performance. You can change your choice anytime via your
            browser settings. Read our{" "}
            <a
              href="/privacy"
              data-testid="cookie-consent-policy-link"
              className="text-[var(--lime)] hover:underline"
            >
              Privacy Policy
            </a>
            .
          </p>

          <div className="mt-5 flex flex-wrap gap-2">
            <button
              data-testid="cookie-consent-accept"
              onClick={() => decide("accepted")}
              className="inline-flex items-center justify-center px-4 py-2.5 bg-[var(--vermilion)] hover:bg-[var(--vermilion-2)] text-white text-xs font-medium tracking-wide rounded-full transition-colors"
            >
              Accept all
            </button>
            <button
              data-testid="cookie-consent-essential"
              onClick={() => decide("essential")}
              className="inline-flex items-center justify-center px-4 py-2.5 border border-[var(--border-dim)] hover:border-[var(--lime)] hover:text-[var(--lime)] text-xs font-medium tracking-wide rounded-full transition-colors"
            >
              Essential only
            </button>
            <button
              data-testid="cookie-consent-decline"
              onClick={() => decide("declined")}
              className="inline-flex items-center justify-center px-4 py-2.5 text-[var(--text-3)] hover:text-[var(--text-1)] text-xs font-medium tracking-wide transition-colors"
            >
              Decline
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
